import React, { useState, useEffect } from 'react';
import { FileText, Eye, Download, X } from 'lucide-react';
import Spinner from './Spinner';
import toast from 'react-hot-toast';
import { errorModal } from './ErrorModal';

interface AttachmentViewerProps {
  isOpen: boolean;
  url: string;
  filename: string;
  onClose: () => void;
}

const AttachmentViewer: React.FC<AttachmentViewerProps> = ({
  isOpen,
  url,
  filename,
  onClose,
}) => {
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [originalAttachmentUrl, setOriginalAttachmentUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && url) {
      loadAttachment();
    } else {
      // Clean up when modal closes
      if (currentAttachmentUrl && currentAttachmentUrl.startsWith('blob:')) {
        window.URL.revokeObjectURL(currentAttachmentUrl);
      }
      setCurrentAttachmentUrl('');
      setOriginalAttachmentUrl('');
    }
  }, [isOpen, url]);

  const loadAttachment = async () => {
    if (!url) {
      errorModal('No attachment URL available');
      return;
    }

    setLoadingAttachment(true);
    setOriginalAttachmentUrl(url);

    try {
      // For direct URLs, fetch and convert to blob URL
      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        try {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Accept': 'application/pdf,image/*,*/*'
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            // Remove PDF toolbar by appending #toolbar=0
            const contentType = response.headers.get('content-type') || '';
            const urlWithToolbarRemoved = contentType.includes('pdf') || filename.toLowerCase().endsWith('.pdf')
              ? `${blobUrl}#toolbar=0`
              : blobUrl;

            setCurrentAttachmentUrl(urlWithToolbarRemoved);
          } else {
            throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError: any) {
          console.error('Fetch failed, opening in new tab:', fetchError);
          // If fetch fails, open in new tab as fallback
          window.open(url, '_blank');
          toast.success('Opening attachment in new tab');
          setLoadingAttachment(false);
          onClose();
          return;
        }
      } else {
        // For relative URLs or other formats, try to open directly
        setCurrentAttachmentUrl(url);
      }
    } catch (error: any) {
      console.error('Error viewing attachment:', error);
      errorModal('Failed to load attachment. Please try again.');
    } finally {
      setLoadingAttachment(false);
    }
  };

  const handleClose = () => {
    // Revoke blob URL if it was created
    if (currentAttachmentUrl && currentAttachmentUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentAttachmentUrl);
    }
    setCurrentAttachmentUrl('');
    setOriginalAttachmentUrl('');
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={{ margin: '0px !important' }}>
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="h-5 w-5 text-white" />
            <h3 className="text-xl font-semibold text-white truncate">
              {filename}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            {originalAttachmentUrl && (
              <button
                onClick={() => {
                  window.open(originalAttachmentUrl, '_blank');
                }}
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <Eye className="h-5 w-5" />
              </button>
            )}
            {currentAttachmentUrl && (
              <a
                href={currentAttachmentUrl}
                download={filename}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Download"
              >
                <Download className="h-5 w-5" />
              </a>
            )}
            <button
              onClick={handleClose}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - iframe */}
        <div className="flex-1 overflow-hidden bg-gray-100 p-4">
          {loadingAttachment ? (
            <div className="flex items-center justify-center h-full">
              <Spinner />
              <span className="ml-3 text-gray-600">Loading attachment...</span>
            </div>
          ) : currentAttachmentUrl ? (
            <iframe
              src={currentAttachmentUrl}
              className="w-full h-full min-h-[600px] rounded-lg shadow-lg border-0"
              title={filename}
              style={{ border: 'none' }}
              onError={(e) => {
                console.error('Iframe load error:', e);
                errorModal('Failed to load in viewer. Opening in new tab...');
                setTimeout(() => {
                  // If it's not a blob URL, open it
                  if (!currentAttachmentUrl.startsWith('blob:')) {
                    window.open(currentAttachmentUrl, '_blank');
                  }
                }, 1000);
              }}
              onLoad={() => {
                // Iframe loaded successfully
                console.log('Iframe loaded successfully');
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
    </div>
  );
};

export default AttachmentViewer;

